import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TrendingUp } from '@mui/icons-material';

import { getScoreBg, getScoreColor, colors, fontSize } from '../../theme';
import { Card, CardTitle, tdSx, thSx, trSx } from './primitives';
import {
  fmtNumber,
  fmtPct,
  fmtPrice,
  getLocalizedText,
  getStateLabel,
  stateColor,
} from './shared';

const rowShape = PropTypes.shape({
  ticker: PropTypes.string,
  state: PropTypes.string,
  state_label: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  score: PropTypes.number,
  score_level: PropTypes.string,
  confidence: PropTypes.string,
  confidence_label: PropTypes.shape({
    vi: PropTypes.string,
    en: PropTypes.string,
  }),
  daily: PropTypes.shape({
    close: PropTypes.number,
  }),
  signal: PropTypes.shape({
    entry: PropTypes.number,
    stop: PropTypes.number,
    target_1: PropTypes.number,
    reward_pct: PropTypes.number,
    rr_ratio: PropTypes.number,
  }),
});

function TopUpsideSection({ rows, onOpenTicker, t, language }) {
  if (!rows.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Card>
        <CardTitle icon={TrendingUp}>
          {t('ichimokuTransition.topUpside.title')}
          <Typography component="span" sx={{ fontSize: fontSize.xs, color: colors.gray[400], ml: 1, fontWeight: 500 }}>
            {t('ichimokuTransition.topUpside.subtitle')}
          </Typography>
        </CardTitle>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...thSx, width: 44 }}>#</TableCell>
                <TableCell sx={thSx}>{t('ichimokuTransition.table.ticker')}</TableCell>
                <TableCell sx={thSx}>{t('ichimokuTransition.table.state')}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'center' }}>{t('ichimokuTransition.table.score')}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('ichimokuTransition.topUpside.upside')}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>{t('ichimokuTransition.table.rr')}</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right', display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('ichimokuTransition.drawer.target1')}
                </TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right', display: { xs: 'none', md: 'table-cell' } }}>
                  {t('ichimokuTransition.table.close')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={row.ticker} hover sx={trSx} onClick={() => onOpenTicker(row.ticker)}>
                  <TableCell sx={{ ...tdSx, color: colors.gray[400], fontWeight: 800, fontSize: fontSize.sm }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 800, color: colors.gray[800] }}>
                    {row.ticker}
                  </TableCell>
                  <TableCell sx={tdSx}>
                    <Chip
                      label={getLocalizedText(row.state_label, language, getStateLabel(t, row.state))}
                      size="small"
                      sx={{
                        bgcolor: `${stateColor(row.state)}14`,
                        color: stateColor(row.state),
                        fontWeight: 700,
                        height: 22,
                        fontSize: fontSize.xs,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Chip
                      label={fmtNumber(row.score, 1)}
                      size="small"
                      sx={{
                        bgcolor: getScoreBg(row.score_level),
                        color: getScoreColor(row.score_level),
                        fontWeight: 800,
                        minWidth: 44,
                        height: 22,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tdSx,
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      fontWeight: 800,
                      color: colors.success.main,
                    }}
                  >
                    {fmtPct(row.signal?.reward_pct)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tdSx,
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: (row.signal?.rr_ratio || 0) >= 1.5 ? colors.success.main : colors.gray[700],
                    }}
                  >
                    {fmtNumber(row.signal?.rr_ratio, 2)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tdSx,
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}
                  >
                    {fmtPrice(row.signal?.target_1)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tdSx,
                      textAlign: 'right',
                      fontFamily: 'monospace',
                      display: { xs: 'none', md: 'table-cell' },
                    }}
                  >
                    {fmtPrice(row.daily?.close)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

TopUpsideSection.propTypes = {
  rows: PropTypes.arrayOf(rowShape).isRequired,
  onOpenTicker: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
};

export default TopUpsideSection;
